// Master-bus AnalyserNode — same connect-fork trick as the recording tap.
//
// The Strudel singleton routes every voice's final node into
// audioCtx.destination. installConnectPatch (player.ts) intercepts those
// connect() calls and forks them to any extra "tap" nodes we've built.
// This module owns one such tap: an AnalyserNode that downstream UI
// (Spectrum, future loudness meters) reads via getByteFrequencyData.
//
// Lazy by design: the node only exists once the AudioContext has been
// captured. Until then `getMasterAnalyser()` returns null and consumers
// degrade silently — the spectrum just paints empty bars.

let masterAnalyser: AnalyserNode | null = null;

const FFT_SIZE = 1024;
const SMOOTHING = 0.78;

/** Create the analyser node against a given context. Idempotent — repeat
 *  calls return the existing node. Called from player.ts after the
 *  AudioContext is captured. */
export function ensureMasterAnalyser(ctx: AudioContext): AnalyserNode | null {
  if (masterAnalyser) return masterAnalyser;
  try {
    const node = ctx.createAnalyser();
    node.fftSize = FFT_SIZE;
    node.smoothingTimeConstant = SMOOTHING;
    masterAnalyser = node;
  } catch {
    masterAnalyser = null;
  }
  return masterAnalyser;
}

/** The shared analyser, or null before boot. Visualizers read this and
 *  call getByteFrequencyData on each ticker frame. */
export function getMasterAnalyser(): AnalyserNode | null {
  return masterAnalyser;
}
